import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import DefaultTextField from 'components/Utils/DefaultTextField';
import ProgressMessage from 'components/Utils/ProgressMessage';
import DocumentTemplate from 'components/HealthRecords/Documents/DocumentTemplate';
import NewDocumentTemplate from 'components/HealthRecords/Documents/NewDocumentTemplate';
import PerformanceUtils from 'services/PerformanceUtils';
import Icon from 'components/Utils/NewIcon';
import DefaultModal from 'components/Utils/DefaultModal';
import './style.scss';
import DefaultButton from 'components/Utils/NewDefaultButton';
import DocumentUtils from 'services/DocumentUtils';
import _ from 'lodash';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import NewPrefilledDocumentTemplate from 'components/HealthRecords/Documents/NewPrefilledDocumentTemplate';
import DefaultTooltip from 'components/Utils/DefaultTooltip';
import { PERMISSIONS } from 'services/StaffUtils';
import RequestDocumentTemplateModal from './RequestDocumentTemplateModal';
import { GetStaff, IsAdmin, IsPhysician } from 'careand-redux/selectors';
import ToggleSwitch from 'components/Utils/ToggleSwitch';
import { useHasPermission, FEATURE_PERMISSIONS } from '../../../../services/FeaturePermissions';
import DocumentTemplateSuggestions from './DocumentTemplateSuggestions';
import config from 'config';

function getDefaultCollapsed(
  collapsed,
  templateCategories,
  showCategory,
  openCategories,
  hideCategories = [],
  showCategories = [],
  startExpanded,
) {
  let updatedCollapsed = { ...collapsed };

  templateCategories.forEach((category) => {
    let enable = !showCategory && !showCategories.includes(category.name);
    let collapsed = enable;

    if (
      (showCategory && category.name === showCategory) ||
      openCategories ||
      showCategories.includes(category.name)
    ) {
      enable = true;
    }

    if (hideCategories.includes(category.name)) enable = false;

    if (startExpanded) collapsed = false;

    if (enable) {
      updatedCollapsed = { ...updatedCollapsed, [category.id]: collapsed };
    }
  });
  return updatedCollapsed;
}

const DocumentTemplateList = (props) => {
  const dispatch = useAppDispatch();

  //TODO undo change before shipping
  // const patientId = '173e6740-082a-4e8e-9660-1e1ab63af39a'
  // const appointment = {
  //   id: 'appointment-id',
  //   patient_id: patientId
  // }
  const appointment = useAppSelector((state) => state.ui.activeBubble?.appointment);
  const isAdmin = useAppSelector(IsAdmin);
  const templates = useAppSelector((state) => state.templates.templates);
  const allTemplateCategories = useAppSelector((state) => state.templates.templateCategories);
  const hasPermission = useHasPermission(FEATURE_PERMISSIONS.templates);
  const hasTemplateEditPermission = useHasPermission({
    permissions: [PERMISSIONS.shift_manager, PERMISSIONS.super_admin],
  });

  const templateCategories = useMemo(() => {
    if (!props.ordering) return allTemplateCategories;

    let result = [];
    props.ordering.forEach((name) => {
      let matching = allTemplateCategories.find((category) => category.name === name);
      if (matching) result.push(matching);
    });
    result = _.uniqBy([...result, ...allTemplateCategories], 'id');
    return result;
  }, [props.ordering, allTemplateCategories]);

  const {
    patient,
    hideCategories,
    showCategory,
    ids,
    openCategories,
    hideSend,
    showCategories,
    startExpanded,
    className = '',
    onClick,
    searchAll,
  } = props;

  const [requestTemplate, setRequestTemplate] = useState(false);
  const [prefilledTemplates, setPrefilledTemplates] = useState(false);
  const prefilledCloseRef = useRef(null);
  const [addingTemplate, setAddingTemplate] = useState(false);
  const [selectedTemplate, updateSelectedTemplate] = useState(undefined);
  const [showOnlyPendingTemplates, setShowOnlyPendingTemplates] = useState(false);
  const [collapsedSecondary, setCollapsedSecondary] = useState({});
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(false);

  // "Pending" covers both explicit template requests and templates whose AI
  // field detection finished and awaits human review — one queue for the admin.
  const isPendingWork = (template) =>
    template.is_pending ||
    template.detection_status === 'completed' ||
    template.detection_status === 'failed';
  const pendingTemplatesCount = templates.filter(isPendingWork).length;

  function setSelectedTemplate(template) {
    if (!canOpenTemplate(template)) {
      return;
    }

    if (onClick) return onClick(template);

    updateSelectedTemplate(template);
  }

  function canOpenTemplate(template) {
    return !template?.is_pending || hasPermission;
  }

  const [collapsed, setCollapsed] = useState(
    getDefaultCollapsed(
      {},
      templateCategories,
      showCategory,
      openCategories,
      hideCategories,
      showCategories,
      startExpanded,
    ),
  );

  useEffect(() => {
    setCollapsed(
      getDefaultCollapsed(
        {},
        templateCategories,
        showCategory,
        openCategories,
        hideCategories,
        showCategories,
        startExpanded,
      ),
    );
  }, [
    templateCategories,
    showCategory,
    openCategories,
    hideCategories,
    showCategories,
    startExpanded,
  ]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      !templateCategories.length
        ? dispatch(api.Templates.getDocumentTemplateCategories())
        : undefined,
      !templates.length ? dispatch(api.Templates.getDocumentTemplates()) : undefined,
    ]).then(() => {
      setLoading(false);
    });
  }, [dispatch]);

  const preparedTemplates = useMemo(() => {
    let show = showCategories;

    if (filter && searchAll) {
      show = [];
    }

    let result = Object.keys(collapsed)
      .map((key) => {
        let category = templateCategories.filter((category) => category.id === key)[0];
        if (show?.length > 0 && !show.includes(category.name)) return;
        return { category, items: [] };
      })
      .filter((item) => !!item);

    const passesToggles = (template) => {
      if (!!ids && !ids.includes(template.id)) return false;
      if (showOnlyPendingTemplates && !isPendingWork(template)) return false;
      return true;
    };

    templates.forEach((template) => {
      result.forEach((category) => {
        if (category.category.id === template.category_id && passesToggles(template)) {
          category.items.push({
            ...template,
            onClick: () => setSelectedTemplate(template),
          });
        }
      });
    });

    // Queue templates can reference a category the FE never loaded (e.g. seeded
    // data or a socket upsert); without a home they'd silently vanish from the
    // filtered view even though the badge counts them.
    if (showOnlyPendingTemplates) {
      const knownIds = new Set(templateCategories.map((category) => category.id));
      const orphans = templates.filter(
        (template) => !knownIds.has(template.category_id) && passesToggles(template),
      );
      if (orphans.length) {
        result.push({
          category: { id: 'uncategorized-queue', name: 'Uncategorized' },
          items: orphans.map((template) => ({
            ...template,
            onClick: () => setSelectedTemplate(template),
          })),
        });
      }
    }

    return result;
  }, [
    onClick,
    templateCategories,
    collapsed,
    templates,
    ids,
    showOnlyPendingTemplates,
    showCategory,
    showCategories,
    searchAll,
    filter,
  ]);

  function toggleCollapsedSecondary(key) {
    setCollapsedSecondary({
      ...collapsedSecondary,
      [key]: !isCollapsed(key, collapsedSecondary),
    });
  }

  function toggleCollapsed(key) {
    setCollapsed({
      ...collapsed,
      [key]: !isCollapsed(key),
    });
  }

  function isCollapsed(key, dictionary = collapsed) {
    if (!key) return false;
    if (filter) return false;
    // The pending queue is a short, filtered list — show its matches expanded,
    // same as an active search.
    if (showOnlyPendingTemplates) return false;
    if (dictionary[key] !== undefined) return dictionary[key];

    return true;
  }

  function passesFilter(value) {
    if (filter === '') return true;
    return value.toLowerCase().indexOf(filter.toLowerCase()) !== -1;
  }

  function getList(categories) {
    let list = [];

    categories.forEach((category) => {
      let collapsed = isCollapsed(category.category.id);
      let chevron = collapsed ? 'chevron-down' : 'chevron-up';
      let categoryContent = [];

      let items = category.items.filter(
        (template) =>
          passesFilter(template.description) ||
          (template.secondary_name && passesFilter(template.secondary_name)) ||
          passesFilter(category.category.name),
      );

      if (items.length > 0) {
        if (!props.showCategory || props.showCategoryTitle) {
          if (props.showCategories?.length !== 1) {
            categoryContent.push(
              <span
                key={category.category.id}
                className={`small-title clickable ${collapsed ? 'close' : 'open'}`}
                onClick={() => {
                  toggleCollapsed(category.category.id);
                }}
              >
                <Icon
                  src={chevron}
                  width="10px"
                  className="mr-2"
                  style={{ display: 'flex', float: 'left' }}
                />{' '}
                {category.category.name}
                <span className="category-count">{items.length}</span>
              </span>,
            );
          }
        }

        let templateList = [];

        let lastSecondaryName = undefined,
          lastTertiaryName = undefined;

        if (props.showSecondaryName) {
          let noSecondary = items.filter((item) => !item.secondary_name);
          let secondary = items.filter((item) => !!item.secondary_name);
          secondary = PerformanceUtils.sortBy(secondary, ['secondary_name'], false);
          items = [...noSecondary, ...secondary];
        }

        if (!!props.secondaryNamesOrder) {
          items = orderSecondaryNames(items, props.secondaryNamesOrder);
        }

        items.forEach((template) => {
          let className = 'template clickable truncate';
          let secondaryClassName = 'secondary-name';
          let tertiaryClassName = 'tertiary-name';
          if (template.is_pending) {
            className += ' is-pending';
          }
          if (collapsed) {
            className = ' hide';
            secondaryClassName = 'hide';
            tertiaryClassName = 'hide';
          }

          let secondaryId = template.category_id + template.secondary_name;
          let isOpenSecondary = true;
          let secondaryName, chevronSecondary, onSecondaryClick, tertiaryName;

          let currentSecondary = (template.secondary_name || '').split('>')[0].trim();
          let currentTertiaryName = ((template.secondary_name || '').split('>')[1] || '').trim();

          if (currentTertiaryName) className += ' tertiary';
          else if (currentSecondary) className += ' secondary';

          if (props.showSecondaryName && !!template.secondary_name) {
            secondaryName = lastSecondaryName !== currentSecondary ? currentSecondary : undefined;
            lastSecondaryName = currentSecondary;

            if (props.secondaryCollapse) {
              isOpenSecondary = !isCollapsed(secondaryId, collapsedSecondary);
              chevronSecondary = isOpenSecondary ? 'chevron-up' : 'chevron-down';
              secondaryClassName += ' clickable';
              secondaryClassName += isOpenSecondary ? ' open' : ' close';

              onSecondaryClick = () => {
                toggleCollapsedSecondary(secondaryId);
              };
            }

            tertiaryName =
              lastTertiaryName !== currentTertiaryName ? currentTertiaryName : undefined;
            lastTertiaryName = currentTertiaryName;
          }

          templateList.push(
            <React.Fragment key={template.id}>
              {!!secondaryName && (
                <li className={secondaryClassName} onClick={onSecondaryClick}>
                  {chevronSecondary ? (
                    <Icon
                      src={chevronSecondary}
                      width="10px"
                      className="mr-2"
                      style={{ display: 'flex', float: 'left' }}
                    />
                  ) : null}{' '}
                  <span className="small-title">{secondaryName}</span>
                </li>
              )}
              {isOpenSecondary && (
                <>
                  {!!tertiaryName && (
                    <li className={tertiaryClassName}>
                      <span className="small-title">{tertiaryName}</span>
                    </li>
                  )}
                  <DefaultTooltip
                    placement="top"
                    title={template.is_pending ? 'Not available yet' : ''}
                  >
                    <li
                      className={'template-item ' + (className || '')}
                      onClick={template.onClick}
                      title={template.description}
                    >
                      {template.is_pending && <Icon src="hourglass" height="14px" />}
                      {template.detection_status === 'processing' && (
                        <Icon src="spinner" height="14px" title="AI detecting fields…" />
                      )}
                      {template.detection_status === 'completed' && (
                        <Icon src="brain-circuit" height="14px" title="AI fields await review" />
                      )}
                      {template.detection_status === 'failed' && (
                        <Icon
                          src="triangle-exclamation"
                          height="14px"
                          title="AI detection failed"
                        />
                      )}
                      {props.getDescription
                        ? props.getDescription(template) || template.description
                        : template.description}
                    </li>
                  </DefaultTooltip>
                </>
              )}
            </React.Fragment>,
          );
        });

        categoryContent.push(
          <ul key={'ul' + category.category.id} className="category">
            {templateList}
          </ul>,
        );

        // Keep the header and its items in one block so the directory layout
        // can flow whole categories into columns without splitting them
        list.push(
          <div key={'block' + category.category.id} className="category-block">
            {categoryContent}
          </div>,
        );
      }
    });

    return list;
  }

  const list = getList(preparedTemplates);

  const defaultSavedDocumentCategory = useMemo(() => {
    if (props.defaultSavedDocumentCategory) return props.defaultSavedDocumentCategory;
    return DocumentUtils.getTemplateMatchingDocumentCategory(selectedTemplate, templateCategories);
  }, [props.defaultSavedDocumentCategory, templateCategories, selectedTemplate]);

  let wrapperClassName = `template-list ${className}`;

  return (
    <div className={wrapperClassName}>
      <TemplateModals
        requestTemplate={requestTemplate}
        setRequestTemplate={setRequestTemplate}
        prefilledTemplates={prefilledTemplates}
        addingTemplate={addingTemplate}
        setPrefilledTemplates={setPrefilledTemplates}
        setAddingTemplate={setAddingTemplate}
        selectedTemplate={selectedTemplate}
        setSelectedTemplate={setSelectedTemplate}
        defaultSavedDocumentCategory={defaultSavedDocumentCategory}
        patient={patient}
        onSave={props.onSave}
        onSaveCompleted={props.onSaveCompleted}
        physician={props.physician}
        hideSend={hideSend}
        allowSave={props.allowSave}
        hidePatientFill={props.hidePatientFill}
        hideFromPatient={props.hideFromPatient}
        showCategories={searchAll && filter ? undefined : showCategories}
        isOwn={!!props.isOwn}
        prefilledCloseRef={prefilledCloseRef}
      />
      {props.hideTitle !== true && props.title && (
        <span className="large-title">Document Templates</span>
      )}
      {loading && <ProgressMessage show={loading} topRight={true} />}
      {hasPermission && !props.hideAddButton && (
        <div className="row" style={{ padding: '5px', gap: '15px' }}>
          {hasTemplateEditPermission && (
            <DefaultButton
              name="Add Template"
              onClick={() => setAddingTemplate(true)}
              style={{ width: '150px' }}
              icon="file-plus"
            />
          )}
          <DefaultButton
            name="Request Template"
            className="request-template-button white"
            onClick={() => setRequestTemplate(true)}
            style={{ width: '200px' }}
            icon="file-plus"
          />
          <DefaultButton
            name="Manage Prefilled Templates"
            onClick={() => setPrefilledTemplates(true)}
            style={{ width: '250px' }}
            icon="file-lines"
          />
          <DefaultButton
            name={showOnlyPendingTemplates ? 'Show All' : `Show Only Pending Requests`}
            unreadCount={showOnlyPendingTemplates ? undefined : pendingTemplatesCount}
            onClick={() => setShowOnlyPendingTemplates((prev) => !prev)}
            style={{ width: '280px' }}
            icon="eye"
          />
        </div>
      )}
      <div className="search-field">
        <Icon src="magnifying-glass" width="20px" className="icon-search" />
        <DefaultTextField
          name="filter"
          label="Search templates"
          value={filter}
          style={{ flexGrow: '1' }}
          onChange={(event) => setFilter(event.target.value)}
        />
        {props.showOpenMode && <OpenMode />}
      </div>
      {config.ENVIRONMENT === 'staging' && appointment && (
        <DocumentTemplateSuggestions appointment={appointment} onClick={setSelectedTemplate} />
      )}
      <div className="main-template-area">
        <ul key="state-resources" className="list-items">
          {list}
        </ul>
      </div>
      {!isAdmin && (
        <DefaultButton
          name="Request Template"
          className="request-template-button white"
          onClick={() => setRequestTemplate(true)}
          style={{ width: '200px', marginTop: '10px' }}
          icon="file-plus"
        />
      )}
    </div>
  );
};

export const OpenMode = () => {
  const dispatch = useAppDispatch();
  const isPhysician = useAppSelector(IsPhysician);
  const ownUser = useAppSelector(GetStaff);

  async function onChangeOpenMode(mode) {
    let preferences = { ...(ownUser.preferences || {}), template_open_mode: mode };
    let physician = { ...ownUser, preferences };
    await dispatch(api.Physicians.updatePhysician({ id: ownUser.id, physician }), {
      errorTitle: 'Favorite Contact',
    });
  }

  const openMode = ownUser?.preferences?.template_open_mode;

  return (
    isPhysician && (
      <ToggleSwitch
        className="template_open_mode"
        element1={<Icon height="20px" src="thumbtack" alt="Pin by default" showTooltip={true} />}
        element2={<Icon height="20px" src="expand" alt="Expand by default" showTooltip={true} />}
        option1="pin"
        option2="expand"
        onChange={(option) => {
          onChangeOpenMode(option);
        }}
        toggled={openMode === 'expand'}
      />
    )
  );
};

const TemplateModals = ({
  requestTemplate,
  setRequestTemplate,
  prefilledTemplates,
  setPrefilledTemplates,
  addingTemplate,
  setAddingTemplate,
  selectedTemplate = {},
  setSelectedTemplate,
  defaultSavedDocumentCategory,
  patient,
  onSave,
  onSaveCompleted,
  physician,
  hideSend,
  allowSave,
  hidePatientFill,
  hideFromPatient,
  showCategories = [],
  isOwn,
  prefilledCloseRef,
}) => (
  <>
    {addingTemplate && (
      <DefaultModal
        className="fit-modal"
        key="new-document-template-modal"
        content={<NewDocumentTemplate onClose={() => setAddingTemplate(false)} />}
        isOpen={addingTemplate}
        onRequestClose={() => setAddingTemplate(false)}
      />
    )}
    {prefilledTemplates && (
      <DefaultModal
        className="large-modal"
        key="new-prefilled-document-template-modal"
        wrap={true}
        title="Prefilled Templates"
        padding={false}
        scrollable={false}
        content={<NewPrefilledDocumentTemplate isOwn={isOwn} onRequestClose={prefilledCloseRef} />}
        isOpen={prefilledTemplates}
        onRequestClose={() => {
          if (prefilledCloseRef.current) {
            prefilledCloseRef.current();
          } else {
            setPrefilledTemplates(false);
          }
        }}
        physician={physician}
      />
    )}
    {!!selectedTemplate.id && (
      <DefaultModal
        className="large-modal"
        key="document-template-modal"
        content={
          <DocumentTemplate
            title={selectedTemplate.description}
            templateId={selectedTemplate.id}
            onClose={() => setSelectedTemplate(undefined)}
            patient={patient}
            physician={physician}
            hideSend={hideSend}
            defaultSavedDocumentCategory={defaultSavedDocumentCategory}
            onSaveCompleted={
              onSaveCompleted ||
              ((document) => {
                setSelectedTemplate(undefined);
                if (onSave) onSave(document);
              })
            }
            hidePatientFill={hidePatientFill}
            allowSave={allowSave}
            hideFromPatient={hideFromPatient}
          />
        }
        isOpen={!!selectedTemplate.id}
        onRequestClose={() => setSelectedTemplate(undefined)}
      />
    )}
    {requestTemplate && (
      <DefaultModal
        title="Request Document Template"
        className="small-modal request-template-modal"
        key="request-template-modal"
        content={
          <RequestDocumentTemplateModal
            onClose={() => setRequestTemplate(false)}
            defaultCategory={showCategories.length === 1 ? showCategories[0] : undefined}
          />
        }
        isOpen={requestTemplate}
        wrap
        padding
        draggable={true}
        onRequestClose={() => setRequestTemplate(false)}
      />
    )}
  </>
);

function orderSecondaryNames(templates, order = []) {
  let sorted = templates.filter((template) => {
    return !order.find((name) => (template.secondary_name || '').startsWith(name));
  });

  let top = [];
  order.forEach((name) => {
    top = [
      ...top,
      ...templates.filter((template) => (template.secondary_name || '').startsWith(name)),
    ];
  });
  return _.uniqBy([...top, ...sorted], 'id');
}

DocumentTemplateList.propTypes = {
  patient: PropTypes.object,
  physician: PropTypes.object,
  showCategory: PropTypes.string,
  hideCategories: PropTypes.array,
  hideTitle: PropTypes.bool,
  title: PropTypes.element,
  showSecondaryName: PropTypes.bool,
  onSave: PropTypes.func,
  onSaveCompleted: PropTypes.func,
  defaultSavedDocumentCategory: PropTypes.string,
  ids: PropTypes.array,
  openCategories: PropTypes.bool,
  secondaryNamesOrder: PropTypes.array,
  secondaryCollapse: PropTypes.bool,
};

export default DocumentTemplateList;
